import React, { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Loader2, UserPlus, RefreshCw, MapPin, CalendarClock, Target, Home as HomeIcon,
  Handshake, StickyNote, Building2, Users, Clock,
} from "lucide-react";

const ROLE_FILTERS = [
  { key: "all", label: "All" },
  { key: "PROFESSIONAL", label: "Professionals" },
  { key: "PROPERTY_SEEKER", label: "Property Seekers" },
  { key: "PROPERTY_OWNER", label: "Property Owners" },
  { key: "REFERRAL_PARTNER", label: "Referral Partners" },
];

const ROLE_BADGE = {
  PROPERTY_SEEKER: "bg-[#EAF4FF] text-[#0F2C59]",
  PROPERTY_OWNER: "bg-emerald-50 text-emerald-700",
  REFERRAL_PARTNER: "bg-amber-50 text-amber-700",
};
const ROLE_LABEL = { PROPERTY_SEEKER: "PROPERTY SEEKER", PROPERTY_OWNER: "PROPERTY OWNER", REFERRAL_PARTNER: "REFERRAL PARTNER" };

const peso = (n) => (n || n === 0 ? `₱${Number(n).toLocaleString()}` : "");
const fmtDate = (iso) => { if (!iso) return null; try { return new Date(iso).toLocaleDateString("en-PH", { month: "short", day: "numeric" }); } catch { return iso; } };

function StatLine({ r }) {
  const s = r.stats || {};
  const bits = [];
  if (r.roles.includes("PROPERTY_SEEKER")) {
    if (r.seeker_type) bits.push(r.seeker_type[0] + r.seeker_type.slice(1).toLowerCase());
    if (r.property_types?.length) bits.push(r.property_types[0]);
    if (r.preferred_locations?.length) bits.push(r.preferred_locations.join(" / "));
    if (r.budget_min || r.budget_max) bits.push(`${peso(r.budget_min)}–${peso(r.budget_max)}`);
    if (s.matched_property_count) bits.push(`${s.matched_property_count} Matched`);
  }
  if (r.roles.includes("PROPERTY_OWNER")) {
    bits.push(`${s.property_count || 0} Properties • ${s.active_property_count || 0} Active`);
    if (s.inquiry_count) bits.push(`${s.inquiry_count} Inquiries`);
  }
  if (r.roles.includes("REFERRAL_PARTNER")) {
    bits.push(`${s.referral_count || 0} Referrals • ${s.referral_qualified_count || 0} Qualified • ${s.referral_won_count || 0} Won`);
  }
  return <div className="text-xs text-slate-500 mt-1 leading-relaxed">{bits.filter(Boolean).join(" · ")}</div>;
}

function RelationshipDetail({ id, onClose, onChanged }) {
  const [r, setR] = useState(null);
  const [note, setNote] = useState("");
  const [fu, setFu] = useState("");
  const load = useCallback(() => { if (id) api.get(`/relationships/${id}`).then((x) => setR(x.data)).catch((e) => toast.error(apiError(e))); }, [id]);
  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/relationships/${id}/notes`, { body: note.trim() }); setNote(""); load(); onChanged?.(); toast.success("Note added"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setFollowup = async () => {
    try { await api.post(`/relationships/${id}/followup`, { next_followup_at: fu || null }); load(); onChanged?.(); toast.success("Follow-up updated"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const opp = r?.opportunities || {};
  const hasOpp = ["buyer_needs", "properties", "leads", "referrals"].some((k) => (opp[k] || []).length);

  return (
    <Sheet open={!!id} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="relationship-detail">
        {!r ? <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
          <>
            <SheetHeader>
              <SheetTitle className="text-[#0F2C59]" data-testid="rel-detail-name">{r.name}</SheetTitle>
            </SheetHeader>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {r.roles.map((role) => <span key={role} className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ROLE_BADGE[role] || "bg-slate-100 text-slate-600"}`}>{ROLE_LABEL[role] || role}</span>)}
            </div>
            <div className="mt-3 text-sm text-slate-600 space-y-1">
              {r.email && <div data-testid="rel-email">{r.email}</div>}
              {r.mobile && <div data-testid="rel-mobile">{r.mobile}</div>}
              {r.next_followup_at && <div className="inline-flex items-center gap-1 text-[#C79A3B] font-medium"><CalendarClock className="h-3.5 w-3.5" /> Follow-up: {fmtDate(r.next_followup_at)}</div>}
            </div>

            <Tabs defaultValue="overview" className="mt-5">
              <TabsList className="w-full justify-start overflow-x-auto">
                <TabsTrigger value="overview" data-testid="rel-tab-overview">Overview</TabsTrigger>
                {hasOpp && <TabsTrigger value="opportunities" data-testid="rel-tab-opportunities">Opportunities</TabsTrigger>}
                <TabsTrigger value="activity" data-testid="rel-tab-activity">Activity</TabsTrigger>
                <TabsTrigger value="notes" data-testid="rel-tab-notes">Notes</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-2 text-sm text-slate-600 mt-4">
                {r.seeker_type && <div><b>Seeker type:</b> {r.seeker_type}</div>}
                {!!r.preferred_locations?.length && <div><b>Locations:</b> {r.preferred_locations.join(", ")}</div>}
                {!!r.property_types?.length && <div><b>Property types:</b> {r.property_types.join(", ")}</div>}
                {(r.budget_min || r.budget_max) && <div><b>Budget:</b> {peso(r.budget_min)}–{peso(r.budget_max)}</div>}
                {r.timeline && <div><b>Timeline:</b> {r.timeline}</div>}
                {r.relationship_source && <div><b>Source:</b> {r.relationship_source}</div>}
                <div className="pt-3 border-t border-slate-100 flex flex-wrap items-end gap-2">
                  <div className="flex-1 min-w-[150px]">
                    <label className="text-xs text-slate-500">Schedule follow-up</label>
                    <Input data-testid="rel-followup-input" type="date" value={fu} onChange={(e) => setFu(e.target.value)} className="rounded-xl mt-1" />
                  </div>
                  <Button data-testid="rel-followup-save" onClick={setFollowup} className="rounded-xl bg-[#0F2C59] hover:bg-[#143163] text-white"><CalendarClock className="h-4 w-4 mr-1.5" /> Save</Button>
                </div>
              </TabsContent>

              {hasOpp && (
                <TabsContent value="opportunities" className="space-y-4 mt-4">
                  {(opp.buyer_needs || []).length > 0 && <OppGroup icon={Target} title="Buyer Needs" items={opp.buyer_needs.map((b) => ({ id: b.id, line: b.title, sub: `${peso(b.budget_min)}–${peso(b.budget_max)} · ${b.status || ""}` }))} />}
                  {(opp.properties || []).length > 0 && <OppGroup icon={HomeIcon} title="Properties" items={opp.properties.map((p) => ({ id: p.id, line: p.title, sub: `${p.city || ""} · ${p.status || ""}` }))} />}
                  {(opp.leads || []).length > 0 && <OppGroup icon={Users} title="Leads" items={opp.leads.map((l) => ({ id: l.id, line: l.contact_name, sub: `${l.lead_type || ""} · ${l.status || ""}` }))} />}
                  {(opp.referrals || []).length > 0 && <OppGroup icon={Handshake} title="Referrals" items={opp.referrals.map((rf) => ({ id: rf.id, line: rf.referral_type || "Referral", sub: rf.status || "" }))} />}
                </TabsContent>
              )}

              <TabsContent value="activity" className="mt-4">
                {!(r.activity || []).length ? <p className="text-sm text-slate-400 py-6 text-center">No activity yet.</p> : (
                  <ul className="space-y-2" data-testid="rel-activity-list">
                    {r.activity.map((a) => <li key={a.id} className="flex items-center gap-2 text-sm text-slate-600"><Clock className="h-3.5 w-3.5 text-slate-400 shrink-0" /><span className="font-medium">{a.type.replace(/_/g, " ").toLowerCase()}</span><span className="text-slate-400 ml-auto text-xs">{fmtDate(a.at)}</span></li>)}
                  </ul>
                )}
              </TabsContent>

              <TabsContent value="notes" className="mt-4">
                <div className="flex gap-2 mb-3">
                  <Textarea data-testid="rel-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a private note…" className="rounded-xl min-h-[44px]" rows={1} />
                  <Button data-testid="rel-note-save" onClick={addNote} className="rounded-xl bg-[#0F2C59] hover:bg-[#143163] text-white self-end"><StickyNote className="h-4 w-4" /></Button>
                </div>
                {!(r.notes || []).length ? <p className="text-sm text-slate-400 py-4 text-center">No notes yet.</p> : (
                  <ul className="space-y-2" data-testid="rel-notes-list">
                    {r.notes.map((n) => <li key={n.id} className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-sm text-slate-700"><div>{n.body}</div><div className="text-[11px] text-slate-400 mt-1">{fmtDate(n.created_at)}</div></li>)}
                  </ul>
                )}
              </TabsContent>
            </Tabs>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function OppGroup({ icon: Icon, title, items }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs font-semibold text-[#0F2C59] uppercase tracking-wide mb-2"><Icon className="h-4 w-4 text-[#C79A3B]" /> {title}</div>
      <ul className="space-y-1.5">
        {items.map((it) => <li key={it.id} className="rounded-xl border border-slate-100 p-2.5 text-sm"><div className="text-slate-800 truncate">{it.line}</div><div className="text-[11px] text-slate-400">{it.sub}</div></li>)}
      </ul>
    </div>
  );
}

function AddRelationshipDialog({ open, onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", mobile: "", roles: [], seeker_type: "" });
  const toggleRole = (role) => setF((p) => ({ ...p, roles: p.roles.includes(role) ? p.roles.filter((x) => x !== role) : [...p.roles, role] }));
  const submit = async () => {
    if (!f.name.trim() || !f.roles.length) { toast.error("Name and at least one role are required"); return; }
    try {
      const body = { name: f.name.trim(), email: f.email, mobile: f.mobile, roles: f.roles };
      if (f.seeker_type) body.seeker_type = f.seeker_type;
      await api.post("/relationships", body);
      toast.success("Relationship saved"); onCreated?.(); onClose();
      setF({ name: "", email: "", mobile: "", roles: [], seeker_type: "" });
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="add-relationship-dialog">
        <SheetHeader><SheetTitle className="text-[#0F2C59]">Add relationship</SheetTitle></SheetHeader>
        <div className="space-y-3 mt-4">
          <Input data-testid="add-rel-name" placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-xl" />
          <Input data-testid="add-rel-email" placeholder="Email (optional)" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="rounded-xl" />
          <Input data-testid="add-rel-mobile" placeholder="Mobile (optional)" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} className="rounded-xl" />
          <div className="flex flex-wrap gap-2">
            {["PROPERTY_SEEKER", "PROPERTY_OWNER", "REFERRAL_PARTNER"].map((role) => (
              <button key={role} type="button" data-testid={`add-rel-role-${role}`} onClick={() => toggleRole(role)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${f.roles.includes(role) ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "bg-white text-[#0F2C59] border-slate-200"}`}>{ROLE_LABEL[role]}</button>
            ))}
          </div>
          <Button data-testid="add-rel-submit" onClick={submit} className="w-full rounded-xl bg-[#0F2C59] hover:bg-[#143163] text-white">Save relationship</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function MyNetworkTab({ renderProfessionals }) {
  const [role, setRole] = useState("all");
  const [summary, setSummary] = useState({});
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadSummary = useCallback(() => api.get("/relationships/summary").then((r) => setSummary(r.data)).catch(() => {}), []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const load = useCallback(() => {
    if (role === "PROFESSIONAL") { setData(null); return; }
    setLoading(true);
    const p = {};
    if (role !== "all") p.role = role;
    api.get("/relationships", { params: p }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [role]);
  useEffect(() => { load(); }, [load]);

  const runBackfill = async () => {
    setBusy(true);
    try { const r = await api.post("/relationships/backfill"); const c = r.data.created; toast.success(`Backfill complete — ${c.PROPERTY_SEEKER} seekers, ${c.REFERRAL_PARTNER} partners`); loadSummary(); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const items = data?.items || [];

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex flex-wrap gap-1.5" data-testid="network-role-filters">
          {ROLE_FILTERS.map((rf) => {
            const count = rf.key === "all" ? summary.all : summary[rf.key];
            return (
              <button key={rf.key} data-testid={`network-filter-${rf.key}`} onClick={() => setRole(rf.key)}
                className={`text-sm font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${role === rf.key ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "bg-white text-[#0F2C59] border-slate-200 hover:bg-slate-50"}`}>
                {rf.label}{count ? ` (${count})` : ""}
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button data-testid="network-backfill" variant="outline" onClick={runBackfill} disabled={busy} className="rounded-xl border-slate-200 text-slate-600">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}<span className="ml-1.5 hidden sm:inline">Import from records</span>
          </Button>
          <Button data-testid="network-add" onClick={() => setAddOpen(true)} className="rounded-xl bg-[#C79A3B] hover:bg-[#b98d33] text-white"><UserPlus className="h-4 w-4 mr-1.5" /> Add</Button>
        </div>
      </div>

      {role === "PROFESSIONAL" ? (
        renderProfessionals()
      ) : loading ? (
        <div className="flex justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
      ) : !items.length ? (
        <div className="text-center py-16 text-slate-400" data-testid="network-empty">
          <Building2 className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium text-slate-500">No relationships yet</p>
          <p className="text-sm mt-1">Add a person, or import Seekers &amp; Referral Partners from your existing records.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {items.map((r) => (
            <button key={r.id} data-testid={`relationship-card-${r.id}`} onClick={() => setDetailId(r.id)}
              className="text-left min-w-0 rounded-2xl border border-slate-200 bg-white p-4 hover:shadow-md hover:border-[#C79A3B]/40 transition-all">
              <div className="flex items-start justify-between gap-2">
                <span className="font-semibold text-[#0F2C59] truncate">{r.name}</span>
                {r.next_followup_at && <span className="text-[11px] text-[#C79A3B] font-medium inline-flex items-center gap-1 shrink-0"><CalendarClock className="h-3 w-3" />{fmtDate(r.next_followup_at)}</span>}
              </div>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {r.roles.map((role2) => <span key={role2} className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${ROLE_BADGE[role2] || "bg-slate-100 text-slate-600"}`}>{ROLE_LABEL[role2] || role2}</span>)}
              </div>
              <StatLine r={r} />
            </button>
          ))}
        </div>
      )}

      <RelationshipDetail id={detailId} onClose={() => setDetailId(null)} onChanged={() => { load(); loadSummary(); }} />
      <AddRelationshipDialog open={addOpen} onClose={() => setAddOpen(false)} onCreated={() => { load(); loadSummary(); }} />
    </div>
  );
}

export default MyNetworkTab;
